import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { useTranslationApp } from "../../lib/i18next";
import useOnboardingChecklist from "../../hooks/useOnboardingChecklist";
import OnboardingChecklistProgressBar from "./OnboardingChecklistProgressBar";
import { useDefaultAppRoute } from "../../hooks/useIsAdvertisersDemo";
import {
  ONBOARDING_CHECKLIST_HEADER_TITLE,
  ONBOARDING_CHECKLIST_PROGRESS_COMPLETED,
  ONBOARDING_CHECKLIST_SKIP_GUIDE,
} from "../../i18n/keysTranslations";

const OnboardingSetupProgress = ({ onNavigate, isCompact = false }) => {
  const { t } = useTranslationApp();
  const navigate = useNavigate();
  const defaultAppRoute = useDefaultAppRoute();
  const {
    completedCount,
    totalItems,
    progress,
    showOnboardingChecklist,
    onSkipGuide,
  } = useOnboardingChecklist();

  if (!showOnboardingChecklist) {
    return null;
  }

  const onClickProgress = () => {
    navigate(defaultAppRoute);
    onNavigate?.();
  };

  const onClickSkipGuide = (event) => {
    event.stopPropagation();
    onSkipGuide();
  };

  const onKeyDownProgress = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClickProgress();
    }
  };

  if (isCompact) {
    const compactLabel = `${t(ONBOARDING_CHECKLIST_HEADER_TITLE)} · ${t(
      ONBOARDING_CHECKLIST_PROGRESS_COMPLETED,
      {
        completed: completedCount,
        total: totalItems,
      }
    )}`;

    return (
      <Tooltip title={compactLabel} placement="right" arrow disableInteractive>
        <Box
          role="button"
          tabIndex={0}
          aria-label={compactLabel}
          onClick={onClickProgress}
          onKeyDown={onKeyDownProgress}
          sx={{
            mt: 1,
            mx: 2,
            mb: 1,
            width: 48,
            height: 48,
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
          }}
        >
          <CircularProgress
            variant="determinate"
            value={progress}
            size={32}
            color="success"
            sx={{ gridArea: "1 / 1" }}
          />
          <Typography
            sx={{
              gridArea: "1 / 1",
              fontSize: 10,
              fontWeight: 600,
              lineHeight: 1,
              color: "text.secondary",
            }}
          >
            {completedCount}/{totalItems}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClickProgress}
      onKeyDown={onKeyDownProgress}
      sx={{
        mt: 1,
        mx: 2,
        mb: 1,
        borderRadius: 3,
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: INPUT_COLOR_BORDER,
        px: 1.5,
        py: 1.5,
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        "&:hover": {
          bgcolor: "grey.100",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: "text.primary",
          }}
        >
          {t(ONBOARDING_CHECKLIST_HEADER_TITLE)}
        </Typography>
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          {completedCount}/{totalItems}
        </Typography>
      </Box>

      <OnboardingChecklistProgressBar
        progress={progress}
        barColor="success.main"
        sx={{ mb: 1 }}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          {t(ONBOARDING_CHECKLIST_PROGRESS_COMPLETED, {
            completed: completedCount,
            total: totalItems,
          })}
        </Typography>
        <Link
          component="button"
          onClick={onClickSkipGuide}
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: "text.secondary",
            textDecoration: "none",
            flexShrink: 0,
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {t(ONBOARDING_CHECKLIST_SKIP_GUIDE)}
        </Link>
      </Box>
    </Box>
  );
};

export default OnboardingSetupProgress;
